import { RegionalSettings, RegionalDetectionResult } from '@/types/regional';

export class RegionalDetector {
  static detect(): RegionalDetectionResult {
    const locale = this.detectLocale();
    const timezone = this.detectTimezone();
    const currency = this.detectCurrency(locale);
    const region = this.detectRegion(locale);
    const country = this.detectCountry(locale);
    
    return {
      locale,
      timezone,
      currency,
      region,
      country,
      confidence: 0.95
    };
  }

  private static detectLocale(): string {
    if (typeof navigator !== 'undefined') {
      return navigator.language || navigator.languages?.[0] || 'en-US';
    }
    return 'en-US';
  }

  private static detectTimezone(): string {
    if (typeof Intl !== 'undefined') {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    return 'UTC';
  }

  private static detectCurrency(locale: string): string {
    const currencyMap: Record<string, string> = {
      'en-US': 'USD',
      'en-GB': 'GBP',
      'de-DE': 'EUR',
      'fr-FR': 'EUR',
      'es-ES': 'EUR',
      'it-IT': 'EUR',
      'nl-NL': 'EUR',
      'pt-PT': 'EUR',
      'ja-JP': 'JPY',
      'ko-KR': 'KRW',
      'zh-CN': 'CNY',
      'zh-TW': 'TWD',
      'ru-RU': 'RUB',
      'hi-IN': 'INR',
      'ar-SA': 'SAR',
      'tr-TR': 'TRY',
      'pl-PL': 'PLN',
      'cs-CZ': 'CZK',
      'hu-HU': 'HUF',
      'ro-RO': 'RON',
      'bg-BG': 'BGN',
      'hr-HR': 'HRK',
      'sk-SK': 'EUR',
      'sl-SI': 'EUR',
      'et-EE': 'EUR',
      'lv-LV': 'EUR',
      'lt-LT': 'EUR',
      'fi-FI': 'EUR',
      'sv-SE': 'SEK',
      'da-DK': 'DKK',
      'no-NO': 'NOK',
      'is-IS': 'ISK',
      'pt-BR': 'BRL',
      'es-MX': 'MXN',
      'es-AR': 'ARS',
      'es-CL': 'CLP',
      'es-CO': 'COP',
      'es-PE': 'PEN',
      'fr-CA': 'CAD',
      'en-CA': 'CAD',
      'en-AU': 'AUD',
      'en-NZ': 'NZD',
      'en-ZA': 'ZAR',
      'th-TH': 'THB',
      'vi-VN': 'VND',
      'id-ID': 'IDR',
      'ms-MY': 'MYR',
      'tl-PH': 'PHP',
      'en-SG': 'SGD',
      'zh-HK': 'HKD'
    };
    
    return currencyMap[locale] || 'USD';
  }

  private static detectRegion(locale: string): string {
    const parts = locale.split('-');
    return parts[1] || 'US';
  }

  private static detectCountry(locale: string): string {
    const countryMap: Record<string, string> = {
      'US': 'United States',
      'GB': 'United Kingdom',
      'DE': 'Germany',
      'FR': 'France',
      'ES': 'Spain',
      'IT': 'Italy',
      'NL': 'Netherlands',
      'PT': 'Portugal',
      'JP': 'Japan',
      'KR': 'South Korea',
      'CN': 'China',
      'TW': 'Taiwan',
      'RU': 'Russia',
      'IN': 'India',
      'SA': 'Saudi Arabia',
      'TR': 'Turkey',
      'PL': 'Poland',
      'CZ': 'Czech Republic',
      'HU': 'Hungary',
      'RO': 'Romania',
      'BG': 'Bulgaria',
      'HR': 'Croatia',
      'SK': 'Slovakia',
      'SI': 'Slovenia',
      'EE': 'Estonia',
      'LV': 'Latvia',
      'LT': 'Lithuania',
      'FI': 'Finland',
      'SE': 'Sweden',
      'DK': 'Denmark',
      'NO': 'Norway',
      'IS': 'Iceland',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'AR': 'Argentina',
      'CL': 'Chile',
      'CO': 'Colombia',
      'PE': 'Peru',
      'CA': 'Canada',
      'AU': 'Australia',
      'NZ': 'New Zealand',
      'ZA': 'South Africa',
      'TH': 'Thailand',
      'VN': 'Vietnam',
      'ID': 'Indonesia',
      'MY': 'Malaysia',
      'PH': 'Philippines',
      'SG': 'Singapore',
      'HK': 'Hong Kong'
    };
    
    const region = this.detectRegion(locale);
    return countryMap[region] || 'United States';
  }

  static createSettings(detection: RegionalDetectionResult): RegionalSettings {
    const dateFormat = this.getDateFormat(detection.locale);
    const timeFormat = this.getTimeFormat(detection.locale);
    const numberFormat = this.getNumberFormat(detection.locale);
    const firstDayOfWeek = this.getFirstDayOfWeek(detection.locale);

    return {
      locale: detection.locale,
      timezone: detection.timezone,
      currency: detection.currency,
      dateFormat,
      timeFormat,
      numberFormat,
      firstDayOfWeek,
      region: detection.region,
      country: detection.country,
      language: detection.locale.split('-')[0]
    };
  }

  private static getDateFormat(locale: string): 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' {
    const usLocales = ['en-US', 'en-PH', 'en-CA'];
    const isoLocales = ['ja-JP', 'ko-KR', 'zh-CN', 'zh-TW', 'hu-HU', 'lt-LT', 'sv-SE'];
    
    if (usLocales.includes(locale)) return 'MM/DD/YYYY';
    if (isoLocales.includes(locale)) return 'YYYY-MM-DD';
    return 'DD/MM/YYYY';
  }

  private static getTimeFormat(locale: string): '12h' | '24h' {
    const twelveHourLocales = ['en-US', 'en-CA', 'en-AU', 'en-NZ', 'en-PH', 'hi-IN', 'ar-SA'];
    return twelveHourLocales.includes(locale) ? '12h' : '24h';
  }

  private static getNumberFormat(locale: string): 'US' | 'EU' | 'IN' {
    const usLocales = ['en-US', 'en-CA', 'en-GB', 'en-AU', 'en-NZ', 'zh-CN', 'zh-TW', 'ja-JP', 'ko-KR'];
    const inLocales = ['hi-IN', 'en-IN'];
    
    if (usLocales.includes(locale)) return 'US';
    if (inLocales.includes(locale)) return 'IN';
    return 'EU';
  }

  private static getFirstDayOfWeek(locale: string): 0 | 1 {
    const sundayFirstLocales = ['en-US', 'en-CA', 'ja-JP', 'ko-KR', 'zh-CN', 'zh-TW', 'ar-SA', 'he-IL'];
    return sundayFirstLocales.includes(locale) ? 0 : 1;
  }
}